import React from 'react';
import { useState, useEffect, useRef, useImperativeHandle } from 'react';
import {motion} from 'framer-motion';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Collapse } from '@mui/material';
import { deletePolicy } from '../../services/policyAPIs';
import Try from '../../charts/try'; 
import { RestrictedToRole } from '../../roles/RestrictedToRole';
import { ExpandMore } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const PolicyCard = React.forwardRef(({ up, id, title, body, onDelete, imageUrls, downloadRef, forceExpanded, type }, ref) => {
  const textContainerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState('auto');
  const chartRef = useRef();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [numbered, setNumbered] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Use forceExpanded if provided
const isExpanded = forceExpanded || expanded;

// Update click handler
const toggleExpanded = () => {
  if (!forceExpanded) {
    setExpanded(!expanded);
  }
};

  useEffect(() => {
    if (imageUrls && imageUrls.length > 0) {
      const textHeight = textContainerRef.current?.clientHeight || 0;
      const img = new Image();
      img.src = imageUrls[0];
      img.onload = () => {
        const firstImageHeight = img.height;
        setContainerHeight(`${Math.max(textHeight, firstImageHeight)}px`);
      };
    }
  }, [imageUrls, body]);

  useImperativeHandle(ref, () => ({
    getChartElement: () => chartRef.current,
  }));

  const handleGoBackToDownload = () => {
    setDownloadDialogOpen(false);
    if (downloadRef && downloadRef.current) {
      downloadRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const lines = body.split('\n').filter(line => line.trim() !== '');
  useEffect(() => {
    const hasNumberedList = lines.some(line => /^[\s\u00A0]*(\d+\.\s+)(.*)/.test(line));
    setNumbered(hasNumberedList);
  }, [body]);

  const renderBody = () => {
    const elements = [];
    let listItems = [];
    let inList = false;
    let listType = null;

    lines.forEach((line, index) => {
      const trimmedLine = line.replace(/\r?\n$/, '');
      const bulletMatch = trimmedLine.match(/^[-*]\s+(.*)/);
      const numberMatch = trimmedLine.match(/^[\s\u00A0]*(\d+\.\s+)(.*)/);

      if (bulletMatch || numberMatch) {
        if (!inList && listItems.length > 0) {
          const ListTag = listType === 'numbered' ? 'div' : 'ul';
          elements.push(<ListTag key={`list-${index}`} style={{ paddingLeft: '30px' }}>{listItems}</ListTag>);
          listItems = [];
        }

        inList = true;
        if (numberMatch) {
          listType = 'numbered';
          listItems.push(
            <div key={`item-${index}`}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                <strong>{numberMatch[1]}{parseBold(numberMatch[2])}</strong>
              </Typography>
            </div>
          );
        } else {
          listType = 'bullet';
          listItems.push(<li key={`item-${index}`}>{parseBold(bulletMatch[1])}</li>);
        }
      } else {
        if (inList && listItems.length > 0) {
          const ListTag = listType === 'numbered' ? 'div' : 'ul';
          elements.push(<ListTag key={`list-${index}`} style={{ paddingLeft: '30px' }}>{listItems}</ListTag>);
          listItems = [];
          inList = false;
          listType = null;
        }
        elements.push(
          <Typography key={`text-${index}`} variant="body1" sx={{ mb: 1 }}>
            {parseBold(line)}
          </Typography>
        );
      }
    });

    if (listItems.length > 0) {
      const ListTag = listType === 'numbered' ? 'div' : 'ul';
      elements.push(<ListTag key={`list-end`} style={{ paddingLeft: '30px' }}>{listItems}</ListTag>);
    }

    return elements;
  };

  function parseBold(text) {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }

  const handleDelete = async () => {
    try {
      await deletePolicy(id);
      if (onDelete) onDelete(id);
    } catch (err) {
      console.error('Failed to delete policy:', err);
      alert('Failed to delete policy. Please try again.');
    }
  };

  const handleUpdate = () => {
    const route = up === 'hr' 
      ? `/hr-handbook/update/${id}` 
      : `/office-policies/update/${id}`;
    if (up === 'hr') {
      setDownloadDialogOpen(true);
    } else {
      navigate(route, { state: { title, body, imageUrls, type } });
    }
  };

  const confirmUpdate = () => {
    setDownloadDialogOpen(false);
    navigate(`/hr-handbook/update/${id}`, { state: { title, body, imageUrls, type: type || 'HR_HANDBOOK' } });
  };

  const ExpandMoreIcon = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  function decodeHtml(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  return (
    <Card
      sx={{
        position: 'relative',
        boxShadow: expanded ? 6 : 3,
        mb: 2,
        borderRadius: '8px',
        transition: 'box-shadow 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: expanded ? 6 : 4,
        },
        '@media print': {
          boxShadow: 'none !important',
          border: '1px solid #ddd', // optional: keep visual separation
        },
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <CardContent sx={{ paddingBottom: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              flexGrow: 1,
              textAlign: 'left',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            {title}
          </Typography>
          {/* View button for everyone */}
          <Box
            sx={{
              display: 'flex',
              opacity: expanded ? 1 : 0,
              transition: 'opacity 0.2s',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }} // keyframes
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: "loop"
              }}
              onClick={(e) => e.stopPropagation()}
            >
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/policies/view', { state: { id } });
              }}
              // sx={{
              //   color: '#4caf50',
              //   boxShadow: 3,
              //   fontWeight: 500,
              //   textTransform: 'none',
              //   background: 'transparent',
              // }}
            sx={{
              position: 'relative',
              color: '#4caf50',
              fontWeight: 500,
              textTransform: 'none',
              background: 'transparent',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',          // ✅ full width from the start
                height: '2px',
                backgroundColor: '#4caf50',
                transition: 'transform 0.3s ease', // optional animation on hover
                transform: 'scaleX(1)',            // initial state
                transformOrigin: 'left'
              },
              '&:hover::after': {
              transform: 'scaleX(1.2)', // e.g. grow slightly on hover
              },
            }}

            >
              Full view
            </Button>
          </motion.div>


            <RestrictedToRole allowedRoles={['DIRECTOR', 'MANAGER', 'HR']}>
              <IconButton
                onClick={handleUpdate}
                sx={{ color: '#2196f3', '&:hover': { color: '#1e88e5' } }}
                aria-label="update policy"
              >
                <Edit />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
                }}
                sx={{ color: '#f44336', '&:hover': { color: '#d32f2f' } }}
                aria-label="delete policy"
              >
                <Delete />
              </IconButton>
            </RestrictedToRole>
          </Box>
          <ExpandMoreIcon
            expand={expanded}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            aria-expanded={expanded}
            aria-label="toggle details"
          >
            <ExpandMore />
          </ExpandMoreIcon>
        </Box>
      </CardContent>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <CardContent
          sx={{
            pt: 0,
            px: 2,
            pb: 2,
            backgroundColor: 'background.paper',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <Divider sx={{ my: 1 }} />

          {numbered && up === 'hr' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                mt: 2,
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: '60%' },
                  '& p': { mb: '1em' },
                  '& ul, & ol': { mt: '0.5em', mb: '1em', pl: '1.5em' },
                  '& li': { mb: '0.5em' },
                  '& strong': { fontWeight: 'bold' },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: decodeHtml(body) }} />
              </Box>
              <Box
                sx={{
                  width: { xs: '100%', md: '40%' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <div ref={chartRef}>
                  <Try text={body} title={title} />
                </div>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                mt: 2,
              }}
            >
              {body.trim() && (
                <Box
                  sx={{
                    width: imageUrls?.length > 0 ? { xs: '100%', md: '60%' } : '100%',
                    '& p': { mb: '1em' },
                    '& ul, & ol': { mt: '0.5em', mb: '1em', pl: '1.5em' },
                    '& li': { mb: '0.5em' },
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: decodeHtml(body) }} />
                </Box>
              )}
              {imageUrls && imageUrls.length > 0 && (
                <Box
                  sx={{
                    width: body.trim() ? { xs: '100%', md: '40%' } : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {imageUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Policy Image ${index + 1}`}
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Collapse>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete the policy "{title}"?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)}>
        <DialogTitle>Download Old Version</DialogTitle>
        <DialogContent>
          Do you want to download the old version of the HR Handbook before updating?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleGoBackToDownload}>
            Go back to download
          </Button>
          <Button onClick={confirmUpdate} color="success">
            Continue to Update
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

export default PolicyCard;